import { renderPlaceholders } from '@servicetim/domain';
import { COMMENT_BODY_MAX, type Comment, type CommentVisibility } from '@servicetim/shared';
import {
  useCreateComment,
  useDeleteComment,
  useMe,
  useTicketComments,
  useUpdateComment,
} from '@servicetim/ui/api';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  describe,
  Label,
  type OptionLoader,
  type PickerOption,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ProblemBanner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  toast,
  useConfirm,
} from '@servicetim/ui/components';
import { AtSign, Check, LoaderCircle, Lock, Pencil, Send, Trash2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { useProjectCannedResponses } from './api';
import { Avatar } from './badges';
import { formatDateTime, relativeTime, shortId } from './format';
import { useTicketContext } from './ticket-context';

/** Comments thread (S-05-08): public / internal, canned responses, mentions, edit and delete own. */
export function CommentsPanel() {
  const { ticket } = useTicketContext();
  const comments = useTicketComments(ticket.id);
  return (
    <div className="space-y-4">
      {comments.error ? <ProblemBanner error={comments.error} size="sm" /> : null}
      {comments.isPending ? <LoaderCircle className="size-4 animate-spin text-fg-subtle" /> : null}
      {comments.isSuccess && comments.items.length === 0 ? (
        <p className="text-fg-muted text-sm">No comments yet.</p>
      ) : null}
      <ol className="space-y-3">
        {comments.items.map((c) => (
          <CommentItem key={c.id} comment={c} />
        ))}
      </ol>
      {comments.hasMore ? (
        <Button
          size="sm"
          variant="outline"
          onClick={comments.loadMore}
          disabled={comments.isLoadingMore}
        >
          Load more
        </Button>
      ) : null}
      <CommentComposer />
    </div>
  );
}

function CommentItem({ comment }: { comment: Comment }) {
  const { ticket, meId, userLabel, canAdmin } = useTicketContext();
  const update = useUpdateComment(ticket.id);
  const remove = useDeleteComment(ticket.id);
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const own = comment.authorId === meId;
  const author = userLabel(comment.authorId) ?? shortId(comment.authorId);

  const save = () =>
    update.mutate(
      { id: comment.id, body: draft.trim() },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success('Comment updated');
        },
        onError: (e) => toast.error(describe(e).title),
      },
    );

  const del = () =>
    confirm({
      title: 'Delete this comment?',
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        await remove.mutateAsync(comment.id);
        toast.success('Comment deleted');
      },
    });

  return (
    <li
      className={
        comment.visibility === 'internal'
          ? 'rounded-md border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-500/30 dark:bg-amber-500/10'
          : 'rounded-md border border-border bg-surface-raised p-3'
      }
      data-visibility={comment.visibility}
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Avatar name={author} />
        <span className="font-medium text-fg">{author}</span>
        <time
          dateTime={comment.createdAt}
          title={formatDateTime(comment.createdAt)}
          className="text-fg-muted text-xs"
        >
          {relativeTime(comment.createdAt)}
        </time>
        {comment.editedAt ? <span className="text-fg-muted text-xs">(edited)</span> : null}
        {comment.visibility === 'internal' ? (
          <Badge variant="warning">
            <Lock />
            Internal
          </Badge>
        ) : null}
        {(own || canAdmin) && !editing ? (
          <span className="ml-auto flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label="Edit comment"
              onClick={() => setEditing(true)}
            >
              <Pencil />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label="Delete comment"
              onClick={() => void del()}
            >
              <Trash2 />
            </Button>
          </span>
        ) : null}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <Textarea
            aria-label="Edit comment"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            maxLength={COMMENT_BODY_MAX}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
              <X />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={update.isPending || draft.trim().length === 0}
            >
              {update.isPending ? <LoaderCircle className="animate-spin" /> : <Check />}
              Save
            </Button>
          </div>
        </div>
      ) : (
        <p className="mt-2 whitespace-pre-wrap break-words text-fg text-sm leading-relaxed">
          {comment.body}
        </p>
      )}
    </li>
  );
}

/** Small user search popover used for `@` mentions (inserts the name, records the id). */
function MentionPicker({
  load,
  onPick,
}: {
  load: OptionLoader;
  onPick: (option: PickerOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PickerOption[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      load(query, controller.signal)
        .then((rows) => {
          if (!controller.signal.aborted) setOptions(rows);
        })
        .catch(() => {
          if (!controller.signal.aborted) setOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query, load]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" aria-label="Mention someone">
          <AtSign />
          Mention
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search people…" />
          <CommandList>
            {loading ? (
              <div className="py-4 text-center text-fg-muted text-sm">Searching…</div>
            ) : (
              <>
                <CommandEmpty>No matches.</CommandEmpty>
                <CommandGroup>
                  {options.map((o) => (
                    <CommandItem
                      key={o.id}
                      value={o.id}
                      onSelect={() => {
                        onPick(o);
                        setOpen(false);
                      }}
                    >
                      <Avatar name={o.label} />
                      <span className="truncate">{o.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CommentComposer() {
  const { ticket, project, loadUsers } = useTicketContext();
  const me = useMe();
  const create = useCreateComment(ticket.id);
  const canned = useProjectCannedResponses(ticket.projectId);
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<CommentVisibility>('public');
  const [mentions, setMentions] = useState<PickerOption[]>([]);
  const [error, setError] = useState<unknown>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bodyId = useId();
  const internalId = useId();
  const cannedId = useId();

  const insertAtCursor = (text: string) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? body.length;
    const next = `${body.slice(0, start)}${text}${body.slice(end)}`;
    setBody(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + text.length, start + text.length);
    });
  };

  const insertCanned = (id: string) => {
    const template = canned.data?.find((c) => c.id === id);
    if (!template) return;
    const rendered = renderPlaceholders(template.body, {
      ticket: {
        key: ticket.key,
        number: ticket.number,
        summary: ticket.summary,
        status: ticket.display.status_id,
        priority: ticket.display.priority_id,
      },
      requester: { name: ticket.display.reporter_id },
      currentUser: me.data
        ? { displayName: me.data.user.displayName, email: me.data.user.email }
        : undefined,
      project: { key: ticket.display.project_id, name: project?.name },
    });
    insertAtCursor(body.length > 0 && !body.endsWith('\n') ? `\n${rendered}` : rendered);
    if (template.visibility === 'internal') setVisibility('internal');
  };

  const mention = (option: PickerOption) => {
    if (!mentions.some((m) => m.id === option.id)) setMentions([...mentions, option]);
    insertAtCursor(`@${option.label} `);
  };

  const submit = async () => {
    const text = body.trim();
    if (text.length === 0) return;
    setError(null);
    try {
      await create.mutateAsync({ body: text, visibility, mentions: mentions.map((m) => m.id) });
      setBody('');
      setMentions([]);
      toast.success(visibility === 'internal' ? 'Internal note added' : 'Comment added');
    } catch (e) {
      setError(e);
    }
  };

  return (
    <form
      className="space-y-2 rounded-md border border-border bg-surface-raised p-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label="Add a comment"
    >
      <Label htmlFor={bodyId} className="sr-only">
        Comment
      </Label>
      <Textarea
        id={bodyId}
        ref={textareaRef}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={COMMENT_BODY_MAX}
        placeholder={
          visibility === 'internal' ? 'Internal note (agents only)…' : 'Reply to the requester…'
        }
        className={
          visibility === 'internal'
            ? 'border-amber-300 bg-amber-50/40 dark:border-amber-500/40 dark:bg-amber-500/10'
            : undefined
        }
      />
      {mentions.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {mentions.map((m) => (
            <Badge key={m.id} variant="secondary">
              @{m.label}
              <button
                type="button"
                aria-label={`Remove mention of ${m.label}`}
                className="ml-0.5 rounded-sm hover:bg-border-strong"
                onClick={() => setMentions(mentions.filter((x) => x.id !== m.id))}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
      {error ? <ProblemBanner error={error} size="sm" /> : null}
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2">
          <Switch
            id={internalId}
            checked={visibility === 'internal'}
            onCheckedChange={(checked) => setVisibility(checked ? 'internal' : 'public')}
          />
          <Label htmlFor={internalId} className="font-normal">
            Internal note
          </Label>
        </span>
        <MentionPicker load={loadUsers} onPick={mention} />
        {canned.data && canned.data.length > 0 ? (
          <span className="flex items-center gap-2">
            <Label htmlFor={cannedId} className="sr-only">
              Canned response
            </Label>
            <Select value="" onValueChange={insertCanned}>
              <SelectTrigger id={cannedId} size="sm" className="w-48">
                <SelectValue placeholder="Insert canned response…" />
              </SelectTrigger>
              <SelectContent>
                {canned.data.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                    {c.visibility === 'internal' ? ' (internal)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>
        ) : null}
        <Button
          type="submit"
          size="sm"
          className="ml-auto"
          disabled={create.isPending || body.trim().length === 0}
        >
          {create.isPending ? <LoaderCircle className="animate-spin" /> : <Send />}
          {visibility === 'internal' ? 'Add note' : 'Comment'}
        </Button>
      </div>
    </form>
  );
}
